import { useEffect, useMemo, useRef, useState } from "react";
import {
	Autocomplete,
	Box,
	TextField,
	Typography,
	Chip,
	Checkbox,
	CircularProgress,
	InputAdornment,
	Grid,
	Pagination,
	useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { useSale } from "./SaleContext";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { MYSQL_LIMITS } from "../../util/constants";
import { createFilterOptions, Util } from "../../util/Util";
import { usePosQuickAdd } from "./usePosQuickAdd";
import RefreshButton from "../../components/RefreshButton";
import { isItemMetered } from "../../components/quantityStepperStock";
import CachedItemImage from "../../components/CachedItemImage";


interface Props {
	bundle?: any;
	compact?: boolean;
	/** Show barcode field beside search (visual POS). */
	showBarcode?: boolean;
}

/**
 * Tile sizing (portrait): short width, taller image, reserved text block.
 * Grows with viewport via clamp(); never below min or above max.
 */
const TILE = {
	minW: 110,
	maxW: 170,
	/** preferred column track: grows with screen */
	col: "clamp(110px, 11vw, 170px)",
	compactCol: "clamp(100px, 10vw, 150px)",
	minH: 230,
	maxH: 310,
	imageMinH: 130,
	imageMaxH: 200,
	footerMinH: 78,
} as const;

/** Tiles per page in the catalog grid. */
const CATALOG_PAGE_SIZE = 24;

const catalogName = (item: any) =>
	item?.ItemName || item?.item_name || item?.Name || item?.item || "—";

const catalogPrice = (item: any) =>
	Number(item?.SellingPrice ?? item?.selling_price ?? item?.price ?? 0);

const catalogStock = (item: any) =>
	Number(item?.Stock ?? item?.stock ?? 0);

const catalogCategory = (item: any) =>
	String(item?.Category ?? item?.category ?? "").trim();

const catalogFamily = (item: any) =>
	String(item?.Family ?? item?.family ?? "").trim();

const catalogSector = (item: any) =>
	String(item?.Sector ?? item?.sector ?? "").trim();

const uniqueSorted = (values: string[]) =>
	Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
		a.localeCompare(b, undefined, { sensitivity: "base" }),
	);

const PosCatalogTile = ({
	item,
	onPick,
	disabled = false,
}: {
	item: any;
	onPick: (item: any) => void;
	disabled?: boolean;
}) => {
	const theme = useTheme();
	const { t } = useUI();
	const isDark = theme.palette.mode === "dark";
	const name = catalogName(item);
	const price = catalogPrice(item);
	const stock = catalogStock(item);
	const metered = isItemMetered(item, true);
	const stockText = metered ? Util.formatNumber(stock) : "—";

	return (
		<Box
			component="button"
			type="button"
			disabled={disabled}
			onClick={() => {
				if (!disabled) onPick(item);
			}}
			aria-label={name}
			aria-disabled={disabled}
			sx={{
				// Kill default button styles that can hide content
				appearance: "none",
				WebkitAppearance: "none",
				border: `1px solid ${theme.palette.divider}`,
				margin: 0,
				padding: 0,
				font: "inherit",
				textAlign: "left",
				cursor: disabled ? "not-allowed" : "pointer",
				opacity: disabled ? 0.55 : 1,
				// Size bounds — grow with screen via grid track, clamp on the tile itself
				width: "100%",
				minWidth: TILE.minW,
				maxWidth: TILE.maxW,
				minHeight: TILE.minH,
				maxHeight: TILE.maxH,
				height: "100%",
				display: "grid",
				// Image grows; footer is fixed-ish so text always has a row
				gridTemplateRows: `minmax(${TILE.imageMinH}px, 1fr) auto`,
				borderRadius: 2,
				overflow: "hidden",
				backgroundColor: isDark ? theme.palette.background.paper : "#ffffff",
				boxShadow: isDark ? 2 : "0 1px 4px rgba(0,0,0,0.12)",
				transition: "transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease",
				"&:hover": disabled
					? undefined
					: {
						transform: "translateY(-2px)",
						boxShadow: isDark ? 6 : "0 6px 16px rgba(0,0,0,0.16)",
						borderColor: theme.palette.primary.main,
					},
				"&:focus-visible": {
					outline: `2px solid ${theme.palette.primary.main}`,
					outlineOffset: 2,
				},
				"&:disabled": {
					pointerEvents: "none",
				},
			}}
		>
			{/* ── Image: placeholder SVG → IDB cache → network ── */}
			<Box
				sx={{
					position: "relative",
					minHeight: TILE.imageMinH,
					maxHeight: TILE.imageMaxH,
					width: "100%",
					overflow: "hidden",
					backgroundColor: isDark ? "#1a1a1a" : "#f0f0f0",
				}}
			>
				<CachedItemImage itemId={item.id} alt={name} lazy />
				{metered && stock <= 0 && (
					<Chip
						size="small"
						label={t("common:inventory.outOfStock")}
						color="error"
						sx={{ position: "absolute", top: 6, right: 6, zIndex: 1 }}
					/>
				)}
			</Box>

			{/* ── Footer: name, then stock + price ── */}
			<Box
				sx={{
					minHeight: TILE.footerMinH,
					flexShrink: 0,
					px: 1,
					py: 1,
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					gap: 0.75,
					// High-contrast solid band — never transparent over the image
					backgroundColor: isDark ? theme.palette.background.paper : "#ffffff",
					borderTop: `1px solid ${theme.palette.divider}`,
					color: isDark ? theme.palette.text.primary : "#1a1a1a",
				}}
			>
				{/* Line 1: product name */}
				<Box
					component="div"
					title={name}
					sx={{
						fontSize: "0.9rem",
						fontWeight: 700,
						lineHeight: 1.25,
						color: isDark ? theme.palette.text.primary : "#111111",
						display: "-webkit-box",
						WebkitLineClamp: 2,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
						wordBreak: "break-word",
						minHeight: "2.25em",
					}}
				>
					{name}
				</Box>

				{/* Line 2: stock (left) · price (right) */}
				<Box
					sx={{
						display: "flex",
						alignItems: "baseline",
						justifyContent: "space-between",
						gap: 0.75,
						width: "100%",
					}}
				>
					<Box
						component="span"
						sx={{
							fontSize: "0.8rem",
							fontWeight: 600,
							color: isDark ? theme.palette.text.secondary : "#555555",
							flex: "1 1 auto",
							minWidth: 0,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{stockText}
					</Box>
					<Box
						component="span"
						sx={{
							fontSize: "0.95rem",
							fontWeight: 800,
							color: theme.palette.primary.main,
							flex: "0 0 auto",
							whiteSpace: "nowrap",
						}}
					>
						{Util.formatNumber(price)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

const PosCatalogGrid = ({ bundle, compact = false, showBarcode = false }: Props) => {
	const {
		items,
		setItems,
		activeTask,
		barcodes,
		setBarcodes,
		itemsSourceId,
		setItemsSourceId,
	} = useSale();
	const { api } = useAuth();
	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const { addCatalogItem } = usePosQuickAdd(bundle);
	const [query, setQuery] = useState("");
	const [barcode, setBarcode] = useState("");
	const [barcodeError, setBarcodeError] = useState(false);
	const [loading, setLoading] = useState(false);
	/** Same default as classic POS (`UI_ENABLE_BARCODE`, default on). */
	const [useBarcode, setUseBarcode] = useState(false);
	const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
	const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
	const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
	const [page, setPage] = useState(1);
	const barcodeRef = useRef<HTMLInputElement>(null);

	const categoryOptions = useMemo(
		() => uniqueSorted(items.map(catalogCategory)),
		[items],
	);
	const familyOptions = useMemo(
		() => uniqueSorted(items.map(catalogFamily)),
		[items],
	);
	const sectorOptions = useMemo(
		() => uniqueSorted(items.map(catalogSector)),
		[items],
	);

	const focusBarcodeField = () => {
		if (!useBarcode || !showBarcode) return;
		// defer so focus wins after state updates / re-render
		requestAnimationFrame(() => {
			barcodeRef.current?.focus();
			barcodeRef.current?.select();
		});
	};

	const pickItem = (catalog: any) => {
		if (loading || !activeTask) return false;
		const added = addCatalogItem(catalog);
		if (added) {
			focusBarcodeField();
		}
		return added;
	};

	const fetchItems = async () => {
		if ((activeTask?.source || -1) < 1) return;
		setLoading(true);
		const res = await api.get(`items/${activeTask?.source}/sales`);
		if (res) {
			if (res.status === 200) {
				setItems(res.data.items);
				setBarcodes(res.data.barcodes || {});
				setItemsSourceId(activeTask?.source ?? null);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchItems);
			}
		}
		setLoading(false);
	};

	// Auto-load once per depot. Shared SaleContext keeps items/barcodes across layout switches.
	useEffect(() => {
		const source = activeTask?.source ?? -1;
		if (source < 1 || loading) return;
		if (itemsSourceId === source && items.length > 0) return;
		void fetchItems();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTask?.source]);

	useEffect(() => {
		setUseBarcode(Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true));
	}, [appSettings?.UI_ENABLE_BARCODE?.value]);

	useEffect(() => {
		if (useBarcode && showBarcode) {
			focusBarcodeField();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [useBarcode, showBarcode]);

	const lookupBarcode = (code: string = barcode) => {
		if (loading || !activeTask) return;
		const trimmed = code.trim();
		if (!trimmed) return;

		const itemId = barcodes[trimmed];
		if (itemId) {
			const catalog = items.find((entry: any) => entry.id === itemId);
			if (catalog) {
				// Show the matched product in the search field
				setQuery(catalogName(catalog));
				pickItem(catalog);
			}
			setBarcode("");
			setBarcodeError(false);
			if (useBarcode) {
				focusBarcodeField();
			}
		} else {
			setBarcodeError(true);
			setTimeout(() => setBarcodeError(false), 2000);
			barcodeRef.current?.focus();
			barcodeRef.current?.select();
		}
	};

	const catalogDisabled = loading || !activeTask;

	/**
	 * Facet filters (category / family / sector) apply only when search is empty.
	 * Active search text or barcode lookup uses the full catalog (prevails over facets).
	 */
	const filtered = useMemo(() => {
		const search = query.trim();
		if (search) {
			const filter = createFilterOptions((opt: any) =>
				`${catalogName(opt)} ${opt.ItemCode || opt.item_code || ""} ${catalogCategory(opt)} ${catalogFamily(opt)} ${catalogSector(opt)}`,
			);
			return filter(items, { inputValue: search });
		}

		let result = items;
		if (selectedCategories.length > 0) {
			result = result.filter((item: any) =>
				selectedCategories.includes(catalogCategory(item)),
			);
		}
		if (selectedFamilies.length > 0) {
			result = result.filter((item: any) =>
				selectedFamilies.includes(catalogFamily(item)),
			);
		}
		if (selectedSectors.length > 0) {
			result = result.filter((item: any) =>
				selectedSectors.includes(catalogSector(item)),
			);
		}
		return result;
	}, [items, query, selectedCategories, selectedFamilies, selectedSectors]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / CATALOG_PAGE_SIZE));

	const paginated = useMemo(() => {
		const safePage = Math.min(page, totalPages);
		const start = (safePage - 1) * CATALOG_PAGE_SIZE;
		return filtered.slice(start, start + CATALOG_PAGE_SIZE);
	}, [filtered, page, totalPages]);

	// Reset to first page when filters / search / catalog source change
	useEffect(() => {
		setPage(1);
	}, [query, selectedCategories, selectedFamilies, selectedSectors, activeTask?.source, items.length]);

	// Clamp page if filtered set shrinks
	useEffect(() => {
		if (page > totalPages) setPage(totalPages);
	}, [page, totalPages]);

	const colTrack = compact ? TILE.compactCol : TILE.col;

	const multiFilterSx = {
		"& .MuiAutocomplete-tag": { maxWidth: 120 },
	} as const;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, gap: 1 }}>
			{/* Category · Family · Sector (multi) — ignored while search is active */}
			<Grid container spacing={1} alignItems="flex-start">
				<Grid size={{ xs: 12, sm: 4 }}>
					<Autocomplete
						multiple
						size="small"
						options={categoryOptions}
						value={selectedCategories}
						onChange={(_, next) => setSelectedCategories(next)}
						disableCloseOnSelect
						disabled={catalogDisabled}
						filterOptions={createFilterOptions((opt: string) => opt)}
						renderInput={(params) => (
							<TextField
								{...params}
								variant="filled"
								label={t("common:tables.category")}
								placeholder={selectedCategories.length ? "" : t("common:pos.layout.filterAny", { defaultValue: "Any" })}
							/>
						)}
						sx={multiFilterSx}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 4 }}>
					<Autocomplete
						multiple
						size="small"
						options={familyOptions}
						value={selectedFamilies}
						onChange={(_, next) => setSelectedFamilies(next)}
						disableCloseOnSelect
						disabled={catalogDisabled}
						filterOptions={createFilterOptions((opt: string) => opt)}
						renderInput={(params) => (
							<TextField
								{...params}
								variant="filled"
								label={t("common:tables.family")}
								placeholder={selectedFamilies.length ? "" : t("common:pos.layout.filterAny", { defaultValue: "Any" })}
							/>
						)}
						sx={multiFilterSx}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 4 }}>
					<Autocomplete
						multiple
						size="small"
						options={sectorOptions}
						value={selectedSectors}
						onChange={(_, next) => setSelectedSectors(next)}
						disableCloseOnSelect
						disabled={catalogDisabled}
						filterOptions={createFilterOptions((opt: string) => opt)}
						renderInput={(params) => (
							<TextField
								{...params}
								variant="filled"
								label={t("common:tables.sector")}
								placeholder={selectedSectors.length ? "" : t("common:pos.layout.filterAny", { defaultValue: "Any" })}
							/>
						)}
						sx={multiFilterSx}
					/>
				</Grid>
			</Grid>

			<Grid container spacing={1} alignItems="flex-start">
				{showBarcode && (
					<Grid size={{ xs: 12, sm: 5, md: 4 }}>
						<TextField
							id="posCatalogBarcode"
							size="small"
							fullWidth
							variant="filled"
							label={
								<span>
									{t("common:ui.labels.barcode")}&nbsp;&nbsp;
									<Checkbox
										size="small"
										checked={useBarcode}
										onChange={() => setUseBarcode((prev) => !prev)}
										// keep label click from stealing focus awkwardly
										onClick={(e) => e.stopPropagation()}
										sx={{ p: 0.25 }}
									/>
								</span>
							}
							value={barcode}
							inputRef={barcodeRef}
							inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
							onChange={(e) => setBarcode(e.target.value)}
							onFocus={(e) => e.target.select()}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									lookupBarcode(barcode);
								}
							}}
							disabled={catalogDisabled || Object.keys(barcodes).length === 0}
							error={barcodeError}
							helperText={barcodeError ? t("common:validation.noBarcodeFound") : " "}
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<FontAwesomeIcon
												icon={faMagnifyingGlass}
												style={{
													cursor: catalogDisabled ? "not-allowed" : "pointer",
													opacity: catalogDisabled ? 0.4 : 1,
												}}
												onClick={() => {
													if (!catalogDisabled) lookupBarcode();
												}}
											/>
										</InputAdornment>
									),
								},
							}}
						/>
					</Grid>
				)}
				<Grid size={{ xs: 12, sm: showBarcode ? 7 : 12, md: showBarcode ? 8 : 12 }}>
					<TextField
						size="small"
						fullWidth
						variant="filled"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						label={t("common:pos.layout.searchCatalog")}
						placeholder={
							loading
								? t("common:ui.placeholders.loadingItems")
								: t("common:pos.layout.searchCatalog")
						}
						helperText=" "
						disabled={catalogDisabled}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end" sx={{ gap: 0.25 }}>
									{query.length > 0 && !catalogDisabled && (
										<IconButton
											size="small"
											tabIndex={-1}
											aria-label={t("common:ui.buttons.clear")}
											onClick={() => setQuery("")}
											edge="end"
										>
											<ClearIcon fontSize="small" />
										</IconButton>
									)}
									<RefreshButton isWorking={loading} onClick={fetchItems} disabled={loading} />
								</InputAdornment>
							),
						}}
					/>
				</Grid>
			</Grid>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "auto",
					display: "grid",
					// Responsive columns: track grows with viewport between min/max
					gridTemplateColumns: `repeat(auto-fill, minmax(${colTrack}, 1fr))`,
					justifyItems: "center",
					alignContent: "start",
					gap: 1.25,
					p: 1,
					position: "relative",
					opacity: catalogDisabled && items.length > 0 ? 0.65 : 1,
					pointerEvents: catalogDisabled ? "none" : "auto",
				}}
			>
				{loading && (
					<Box
						sx={{
							position: items.length === 0 ? "static" : "absolute",
							inset: 0,
							gridColumn: items.length === 0 ? "1 / -1" : undefined,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							py: items.length === 0 ? 6 : 0,
							zIndex: 2,
							bgcolor: items.length === 0 ? "transparent" : "rgba(0,0,0,0.04)",
						}}
					>
						<CircularProgress size={32} />
					</Box>
				)}
				{!loading && filtered.length === 0 ? (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ gridColumn: "1 / -1", textAlign: "center", py: 4 }}
					>
						{t("common:pos.layout.noItems")}
					</Typography>
				) : (
					paginated.map((item: any) => (
						<PosCatalogTile
							key={item.id}
							item={item}
							onPick={pickItem}
							disabled={catalogDisabled}
						/>
					))
				)}
			</Box>

			{filtered.length > 0 && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
						px: 0.5,
						py: 0.5,
						flexShrink: 0,
						borderTop: 1,
						borderColor: "divider",
					}}
				>
					<Typography variant="caption" color="text.secondary" fontWeight={600}>
						{t("common:pos.layout.pageSummary", {
							defaultValue: "{{from}}–{{to}} of {{total}}",
							from: Util.formatNumber(Math.min((page - 1) * CATALOG_PAGE_SIZE + 1, filtered.length)),
							to: Util.formatNumber(Math.min(page * CATALOG_PAGE_SIZE, filtered.length)),
							total: Util.formatNumber(filtered.length),
						})}
					</Typography>
					<Pagination
						size="small"
						color="primary"
						count={totalPages}
						page={Math.min(page, totalPages)}
						onChange={(_, next) => setPage(next)}
						disabled={catalogDisabled || totalPages <= 1}
						showFirstButton
						showLastButton
					/>
				</Box>
			)}
		</Box>
	);
};

export default PosCatalogGrid;
